/**
 * Project Store (Zustand)
 * Manages projects, files, and navigation state.
 * Extracted from App.tsx to enable feature-level access.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Project, TranslationFile } from '../types';

// Expose store globally for debugging (F12 console: __store.getState().files)
declare global {
    interface Window { __store?: any; }
}

interface ProjectState {
    // Data
    projects: Project[];
    files: TranslationFile[];
    deletedProjectIds: string[];  // Blacklist: IDs of projects deleted but possibly still on disk

    // Navigation
    selectedProjectId: string | null;
    selectedFileId: string | null;

    // Project Actions
    addProject: (project: Project) => void;
    updateProject: (project: Project) => void;
    deleteProject: (projectId: string) => void;
    selectProject: (projectId: string | null) => void;
    markProjectDeleted: (projectId: string) => void;  // Add to blacklist without removing from store

    // File Actions
    addFile: (file: TranslationFile) => void;
    addFiles: (files: TranslationFile[]) => void;
    updateFile: (fileId: string, updates: Partial<TranslationFile>) => void;
    deleteFile: (fileId: string) => void;
    selectFile: (fileId: string | null) => void;

    // Bulk Operations
    setProjects: (projects: Project[]) => void;
    setFiles: (files: TranslationFile[]) => void;
    setProjectsAndFiles: (projects: Project[], files: TranslationFile[]) => void;

    // Selectors
    getProjectFiles: (projectId: string) => TranslationFile[];
    getTrainFiles: (projectId: string) => TranslationFile[];
    getTranslateFiles: (projectId: string) => TranslationFile[];
}

// Helper to reset stale running statuses on load
const resetStaleStatuses = (files: TranslationFile[]): TranslationFile[] => {
    // Cloud sync mode: server vẫn chạy, batchInfo trên disk đã có progress x/y → KHÔNG flip running → error.
    // UI tự render BatchStatusPanel + button "Đang chạy" từ status='running' đã persist.
    // Incognito mode: không có pending_sync → flip để user biết phải retry.
    let isCloudMode = true;
    try {
        // Lazy require để tránh circular import — usePrivacyStore persist localStorage nên đọc được sync.
        const raw = typeof window !== 'undefined' ? localStorage.getItem('trans-bctn-privacy') : null;
        if (raw) {
            const parsed = JSON.parse(raw);
            isCloudMode = parsed?.state?.mode !== 'incognito';
        }
    } catch { /* fallback: cloud */ }

    return files.map(f => {
        const file = { ...f };
        let wasInterrupted = false;

        // Cloud mode: server vẫn xử lý sau refresh (translate/match/audit qua chain).
        // useBatchRecovery + jobs Realtime sẽ resolve trạng thái thật.
        // → Giữ 'running' cho cả 3 status (translate/match/audit) trong cloud mode.
        // Incognito mode: không có pending_sync recovery → flip để user retry.
        if (file.translationStatus === 'running' && !isCloudMode) {
            file.translationStatus = 'error';
            file.error = 'Phiên trước bị gián đoạn. Bấm để thử lại.';
            wasInterrupted = true;
        }
        if (file.auditStatus === 'running' && !isCloudMode) {
            file.auditStatus = 'error';
            wasInterrupted = true;
        }
        if (file.matchStatus === 'running' && !isCloudMode) {
            file.matchStatus = 'error';
            wasInterrupted = true;
        }

        // Fix currentStep — decide từ STATUS fields (light, persist trên state.json),
        // KHÔNG dùng heavy fields (auditResult/semanticMatchResult/aiOutput) vì lazy-load.
        // Trigger reset CHỈ khi:
        //   - Incognito interrupted (wasInterrupted=true) → reset từ light status fields
        //   - Step 2.5 NHƯNG cả 3 status đều terminal (done/error) → train chain đã xong, fix step
        // Skip nếu chain đang running (cloud mode) — giữ currentStep=2.5 cho UI hiện đúng tiến độ.
        const allTerminal =
            (file.matchStatus === 'done' || file.matchStatus === 'error') &&
            (file.translationStatus === 'done' || file.translationStatus === 'error') &&
            (file.auditStatus === 'done' || file.auditStatus === 'error' || file.auditStatus === 'pending');
        if (wasInterrupted || (file.currentStep === 2.5 && allTerminal)) {
            if (file.auditStatus === 'done' || file.auditScore != null) {
                file.currentStep = 4;
            } else if (file.matchStatus === 'done') {
                file.currentStep = 3;
            } else if (file.translationStatus === 'done') {
                file.currentStep = 3;
            } else {
                file.currentStep = 1;
            }
        }

        return file;
    });
};

export const useProjectStore = create<ProjectState>()(
    persist(
        (set, get) => ({
            // Initial State
            projects: [],
            files: [],
            deletedProjectIds: [],
            selectedProjectId: null,
            selectedFileId: null,

            // Project Actions
            addProject: (project) => set(state => ({
                projects: [...state.projects, project]
            })),

            updateProject: (project) => set(state => ({
                projects: state.projects.map(p => p.id === project.id ? project : p)
            })),

            deleteProject: (projectId) => set(state => ({
                projects: state.projects.filter(p => p.id !== projectId),
                files: state.files.filter(f => f.projectId !== projectId),
                selectedProjectId: state.selectedProjectId === projectId ? null : state.selectedProjectId,
                deletedProjectIds: [...state.deletedProjectIds, projectId]
            })),

            selectProject: (projectId) => set({ selectedProjectId: projectId }),

            markProjectDeleted: (projectId) => set(state => ({
                deletedProjectIds: [...state.deletedProjectIds, projectId]
            })),

            // File Actions
            // US-19-16: new files added via addFile/addFiles have content in memory (just uploaded/parsed),
            // so default contentLoaded=true to enable autosave heavy artifact writes.
            // Disk-loaded files (via loadProjects metadata mode) explicitly set contentLoaded=false.
            addFile: (file) => set(state => ({
                files: [...state.files, { contentLoaded: true, ...file }]
            })),

            addFiles: (newFiles) => set(state => {
                // Deduplicate: skip files with same fileName+projectId already in store
                const existingKeys = new Set(
                    state.files.map(f => `${f.projectId}::${f.fileName}`)
                );
                const uniqueNewFiles = newFiles
                    .filter(f => !existingKeys.has(`${f.projectId}::${f.fileName}`))
                    .map(f => ({ contentLoaded: true, ...f })); // US-19-16 default contentLoaded
                if (uniqueNewFiles.length < newFiles.length) {
                    console.warn(`[addFiles] Skipped ${newFiles.length - uniqueNewFiles.length} duplicate files`);
                }
                console.log(`[addFiles] Adding ${uniqueNewFiles.length} files (from ${newFiles.length} input). Store had ${state.files.length} files.`);
                return { files: [...state.files, ...uniqueNewFiles] };
            }),

            updateFile: (fileId, updates) => set(state => ({
                files: state.files.map(f => f.id === fileId ? { ...f, ...updates } : f)
            })),

            deleteFile: (fileId) => set(state => ({
                files: state.files.filter(f => f.id !== fileId),
                selectedFileId: state.selectedFileId === fileId ? null : state.selectedFileId
            })),

            selectFile: (fileId) => set({ selectedFileId: fileId }),

            // Bulk Operations
            setProjects: (projects) => set({ projects }),
            setFiles: (files) => set({ files: resetStaleStatuses(files) }),
            setProjectsAndFiles: (projects, files) => set(state => {
                // Filter out any projects in the blacklist
                const blacklist = state.deletedProjectIds;
                const filteredProjects = projects.filter(p => !blacklist.includes(p.id));
                const filteredProjectIds = new Set(filteredProjects.map(p => p.id));
                const filteredFiles = files.filter(f => filteredProjectIds.has(f.projectId));

                console.log(`[setProjectsAndFiles] Received ${projects.length} projects, blacklist has ${blacklist.length} IDs, showing ${filteredProjects.length}`);

                return {
                    projects: filteredProjects,
                    files: resetStaleStatuses(filteredFiles)
                };
            }),

            // Selectors
            getProjectFiles: (projectId) => {
                return get().files.filter(f => f.projectId === projectId);
            },

            getTrainFiles: (projectId) => {
                const state = get();
                const project = state.projects.find(p => p.id === projectId);
                return state.files.filter(f =>
                    f.projectId === projectId &&
                    (f.humanBenchmark || project?.humanBenchmark)
                );
            },

            getTranslateFiles: (projectId) => {
                const state = get();
                const project = state.projects.find(p => p.id === projectId);
                return state.files.filter(f =>
                    f.projectId === projectId &&
                    !f.humanBenchmark && !project?.humanBenchmark
                );
            }
        }),
        {
            name: 'trans-bctn-project-store',
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({
                // Only persist selection state + deleted blacklist to localStorage
                // Heavy data (projects, files) is persisted to File System via syncFromDisk
                selectedProjectId: state.selectedProjectId,
                selectedFileId: state.selectedFileId,
                deletedProjectIds: state.deletedProjectIds
            }),
            // Custom merge: ONLY accept selection IDs + deleted blacklist from localStorage.
            // This prevents stale projects/files from old localStorage data being restored.
            merge: (persistedState: any, currentState: ProjectState) => ({
                ...currentState,
                selectedProjectId: persistedState?.selectedProjectId ?? null,
                selectedFileId: persistedState?.selectedFileId ?? null,
                deletedProjectIds: persistedState?.deletedProjectIds ?? []
            })
        }
    )
);

// Dev debug: expose store + monitor aiOutput changes
if (typeof window !== 'undefined') {
    (window as any).__store = useProjectStore;
    let prevLens = new Map<string, number>();
    useProjectStore.subscribe(state => {
        state.files.forEach(f => {
            const cur = f.aiOutput?.length || 0;
            const prev = prevLens.get(f.id);
            if (prev !== undefined && prev > 0 && cur === 0) {
                console.warn(`[DEBUG] aiOutput CLEARED for ${f.fileName}: ${prev} -> 0`);
                console.trace('Stack trace');
            }
            prevLens.set(f.id, cur);
        });
    });
}
